import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { ExpandMore } from '@mui/icons-material';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import { FakeLink } from '../../../components/FakeLink';
import { ProtectedContent } from '../../../components/ProtectedContent';
import COLORS from '../../../constants/colors';
import { locations } from '../../../routes/locations';
import { isSelectedItem, selectOpenedParentItem } from './helpers';
import {
  CustomListItemButton,
  Drawer,
  DrawerHeader,
  SIDEBAR,
  scrollAreaSx,
  sidebarHeaderHeight,
} from './SidebarElements';
import { MenuItem, MenuItemParent, MenuItems, MenuItemSimple } from './types';
import packageJson from '../../../../package.json';

/** The drawer toggle now lives in the sidebar header, so it matches the nav rows. */
const toggleButtonSx = {
  width: '32px',
  height: '32px',
  flexShrink: 0,
  borderRadius: '8px',
  color: SIDEBAR.idleIcon,
  '& .MuiSvgIcon-root': { fontSize: '20px' },
  '&:hover': {
    backgroundColor: SIDEBAR.hoverSurface,
    color: COLORS.MAIN_DARK,
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${COLORS.PRIMARY_BLUE}`,
    outlineOffset: '-2px',
  },
} as const;

interface ISidebarProps {
  open: boolean;
  handleDrawerClose: () => void;
  handleDrawerOpen?: () => void;
  theme: Theme;
  menuItems: MenuItems;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function Sidebar({ open, handleDrawerClose, handleDrawerOpen, theme, menuItems }: ISidebarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const openedParentItem = selectOpenedParentItem(menuItems, pathname);
  const [openItems, setItemsOpen] = React.useState<{ [prop: string]: boolean }>(
    openedParentItem ? { [openedParentItem.title]: true } : {},
  );

  React.useEffect(() => {
    const next = selectOpenedParentItem(menuItems, pathname);
    if (next) {
      setItemsOpen((prev) => ({ ...prev, [next.title]: true }));
    }
  }, [menuItems, pathname]);

  const onClick = (menuItem: MenuItemSimple) => {
    if ('path' in menuItem) {
      navigate(menuItem.path);
      return;
    }

    menuItem.onClick();
  };

  /** In the collapsed rail the label is hidden, so the row needs a tooltip instead. */
  const withRailTooltip = (title: string, element: React.ReactElement) => {
    if (open) {
      return element;
    }

    return (
      <Tooltip title={title} placement="right" enterDelay={200}>
        {element}
      </Tooltip>
    );
  };

  const renderItemIcon = (menuItem: MenuItem) => (
    <ListItemIcon
      sx={{
        minWidth: 0,
        mr: open ? '12px' : 0,
        width: '20px',
        height: '20px',
        justifyContent: 'center',
      }}
    >
      <menuItem.IconComponent />
    </ListItemIcon>
  );

  const renderSimpleMenuItemLine = (menuItem: MenuItemSimple) => (
    <>
      {renderItemIcon(menuItem)}
      <ListItemText primary={menuItem.title} sx={{ my: 0, display: open ? 'block' : 'none' }} />
    </>
  );

  const renderSimpleMenuItem = (menuItem: MenuItemSimple, depth: 0 | 1) => {
    const selected = isSelectedItem(menuItem, pathname);

    return (
      <ProtectedContent {...(menuItem.permissionProps || { items: [] })} key={`simple-menu-item-${menuItem.title}`}>
        {withRailTooltip(
          menuItem.title,
          <CustomListItemButton
            selected={selected}
            onClick={() => onClick(menuItem)}
            data-depth={depth}
            aria-current={selected && 'path' in menuItem ? 'page' : undefined}
            sx={{ justifyContent: open ? 'flex-start' : 'center', mb: '2px' }}
          >
            {'path' in menuItem ? (
              <FakeLink href={menuItem.path} style={{ justifyContent: open ? 'flex-start' : 'center' }}>
                {renderSimpleMenuItemLine(menuItem)}
              </FakeLink>
            ) : (
              renderSimpleMenuItemLine(menuItem)
            )}
          </CustomListItemButton>,
        )}
      </ProtectedContent>
    );
  };

  const handleParentClick = (itemKey: string) => {
    // In the rail there is nowhere to show children, so expanding also opens the drawer.
    if (!open && handleDrawerOpen) {
      handleDrawerOpen();
      setItemsOpen((prev) => ({ ...prev, [itemKey]: true }));
      return;
    }

    setItemsOpen((prev) => ({ ...prev, [itemKey]: !prev[itemKey] }));
  };

  const renderParentMenuItem = (menuItem: MenuItemParent) => {
    const expanded = Boolean(openItems[menuItem.title]) && open;
    const ownsActiveRoute = openedParentItem?.title === menuItem.title;
    const sectionId = `sidebar-section-${menuItem.title.replace(/\s+/g, '-').toLowerCase()}`;

    return (
      <ProtectedContent {...(menuItem.permissionProps || { items: [] })} key={`parent-item-${menuItem.title}`}>
        <React.Fragment>
          {withRailTooltip(
            menuItem.title,
            <CustomListItemButton
              onClick={() => handleParentClick(menuItem.title)}
              data-section={ownsActiveRoute ? 'active' : undefined}
              aria-expanded={expanded}
              aria-controls={sectionId}
              sx={{ justifyContent: open ? 'flex-start' : 'center', mb: '2px' }}
            >
              {renderItemIcon(menuItem)}
              <ListItemText primary={menuItem.title} sx={{ my: 0, display: open ? 'block' : 'none' }} />
              {open && (
                <ExpandMore
                  sx={{
                    fontSize: '18px',
                    color: SIDEBAR.idleIcon,
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: theme.transitions.create('transform', {
                      duration: theme.transitions.duration.shortest,
                    }),
                  }}
                />
              )}
            </CustomListItemButton>,
          )}
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box
              id={sectionId}
              sx={{
                ml: '22px',
                pl: '10px',
                mb: '4px',
                borderLeft: `1px solid ${SIDEBAR.border}`,
              }}
            >
              <List component="div" disablePadding>
                {menuItem.children.map((child) => renderSimpleMenuItem(child, 1))}
              </List>
            </Box>
          </Collapse>
        </React.Fragment>
      </ProtectedContent>
    );
  };

  const renderItem = (menuItem: MenuItem) => {
    if ('children' in menuItem) {
      return renderParentMenuItem(menuItem);
    }

    return renderSimpleMenuItem(menuItem, 0);
  };

  return (
    <Drawer variant="permanent" open={open} elevation={0}>
      <DrawerHeader
        sx={{
          height: `${sidebarHeaderHeight}px!important`,
          minHeight: `${sidebarHeaderHeight}px!important`,
          flexShrink: 0,
          // Expanded: logo and toggle share one row. Collapsed: the 96px rail is
          // too narrow for both side by side, so they stack instead.
          flexDirection: open ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          gap: open ? 0 : '6px',
          px: open ? '20px' : 0,
          borderBottom: `1px solid ${SIDEBAR.border}`,
        }}
      >
        <img
          style={{
            transition: 'width .3s',
            // eslint-disable-next-line no-nested-ternary
            width: open ? (isArabic ? '130px' : '155px') : '48px',
            objectFit: 'cover',
            height: '32px',
            objectPosition: isArabic ? '100% 0' : '0 0',
            cursor: 'pointer',
          }}
          onClick={() => navigate(locations.dashboard())}
          src={isArabic ? '/images/logo-with-text-ar.png' : '/images/logo-with-text-en.png'}
          alt="madkhol logo"
        />
        <Tooltip title={open ? 'Collapse menu' : 'Expand menu'} placement={open ? 'bottom' : 'right'} enterDelay={200}>
          <IconButton
            aria-label={open ? 'Collapse menu' : 'Expand menu'}
            aria-expanded={open}
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            sx={toggleButtonSx}
          >
            {open ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        </Tooltip>
      </DrawerHeader>

      <Box
        component="nav"
        aria-label="Main navigation"
        sx={{ ...scrollAreaSx, flex: '1 1 auto', px: open ? '12px' : '10px', py: '12px' }}
      >
        {menuItems.map((items, listIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`main-menu-item-${listIndex}`}>
            {listIndex > 0 && <Divider sx={{ my: '8px', borderColor: SIDEBAR.border }} />}
            <List component="div" disablePadding sx={{ width: '100%' }}>
              {items.map(renderItem)}
            </List>
          </React.Fragment>
        ))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: '16px',
          py: '10px',
          borderTop: `1px solid ${SIDEBAR.border}`,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: SIDEBAR.muted, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
          {open ? `Version ${packageJson.version}` : packageJson.version}
        </Typography>
      </Box>
    </Drawer>
  );
}

export default Sidebar;
